import React, { useCallback, useEffect, useState } from 'react';

import ActionButton from 'components/action-button';
import { DrawerPage } from 'components/layout/drawer-page';
import BlockRenderer from 'components/sanity-block-renderer';
import { toaster } from 'components/toaster/toaster';
import { useSyncUserCommunicationPreferencesMutation } from 'generated/graphql-gateway';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { useIosAtt } from 'hooks/use-ios-att';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { withAuthRequired } from 'pages/authentication/components/auth-required';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useScrollViewFullHeight } from 'state/scroll/hooks';
import { useUIContext } from 'state/ui';
import { isNativeIOS } from 'utils/environment';
import WebLocalStorage from 'utils/local-storage';
import { Keys } from 'utils/local-storage/constants';

import CommCardEmailPush from './comm-card-email-push';

const CommPreferences = () => {
  useScrollViewFullHeight();
  const {
    setModalAuthState,
    signOut,
    user,
    setIsReAuthenticating,
    closeModalAuth,
  } = useAuthContext();
  const { setUserEmail } = useUIContext();
  const requestIosAttPermissions = useIosAtt();

  const smsSubscriptionEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMS_MARKETING_SUBSCRIPTIONS);
  const { featureDisclaimers, featureDisclaimerLoading } = useFeatureDisclaimers();
  const { communicationPreferencesBlock } = featureDisclaimers || {};
  const [marketingEmailPrefs, setMarketingEmailPrefs] = useState<boolean>(false);
  const [pushPrefs, setPushPrefs] = useState<boolean>(false);
  const [marketingSmsPrefs, setMarketingSmsPrefs] = useState<boolean>(false);
  const [isLoadingUserPrefs, setIsLoadingUserPrefs] = useState<boolean>(true);
  const [onUpdatePhoneNumber, setOnUpdatePhoneNumber] = useState<() => Promise<void>>();
  const [isNewPhoneNumberValid, setIsNewPhoneNumberValid] = useState(false);
  const [isUpdatingUserInfo, setIsUpdatingUserInfo] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState<string | null>(null);
  const isMounted = useIsComponentMounted();

  const { email, phoneNumber } = user?.details || {};

  const [
    syncUserCommunicationPreferencesMutation,
    { loading: syncUserCommPrefsLoading },
  ] = useSyncUserCommunicationPreferencesMutation();

  const loadUserPreferences = async () => {
    const { data } = await syncUserCommunicationPreferencesMutation();

    // We rely on these values stored in local storage so that, while the subscription request
    // is being processed (which may take several minutes), we continue to display the toggle button as ON.
    const marketingSmsSubscription = WebLocalStorage.getItemWithExp(
      Keys.MARKETING_SMS_SUBSCRIPTION_REQUESTED
    );
    const marketingEmailSubscription = WebLocalStorage.getItemWithExp(
      Keys.MARKETING_EMAIL_SUBSCRIPTION_REQUESTED
    );
    const marketingPushSubscription = WebLocalStorage.getItemWithExp(
      Keys.MARKETING_PUSH_SUBSCRIPTION_REQUESTED
    );

    setMarketingEmailPrefs(
      marketingEmailSubscription ?? !!data?.syncUserCommunicationPreferences?.marketingEmail
    );
    setPushPrefs(
      marketingPushSubscription ?? !!data?.syncUserCommunicationPreferences?.marketingPush
    );
    setMarketingSmsPrefs(
      marketingSmsSubscription ?? !!data?.syncUserCommunicationPreferences?.marketingSms
    );

    setIsLoadingUserPrefs(false);
  };

  const cleanup = useCallback(() => {
    setIsReAuthenticating(false);
    // Clears user email field in the sign-in form.
    setUserEmail('');
    closeModalAuth();

    setNewPhoneNumber(null);
  }, [closeModalAuth, setIsReAuthenticating, setUserEmail]);

  const modalAuthOnClose = useCallback(() => {
    cleanup();

    toaster.negative(t('authError'));
  }, [cleanup]);

  const signOutGlobally = useCallback(async () => {
    setIsReAuthenticating(true);

    await signOut(true);
  }, [signOut, setIsReAuthenticating]);

  const handleUpdateUser = useCallback(async () => {
    setIsUpdatingUserInfo(true);
    onUpdatePhoneNumber && (await onUpdatePhoneNumber());
    setIsUpdatingUserInfo(false);

    toaster.positive(t('successfullyUpdatedUserAccountInfo'));

    cleanup();
  }, [cleanup, onUpdatePhoneNumber]);

  const showModalAuth = useCallback(() => {
    setUserEmail(email ?? '');

    setModalAuthState({
      screen: ModalAuthScreen.SIGN_IN,
      trigger: ModalAuthTrigger.Global,
      disableEmailInput: true,
      heading: t('pleaseReAuthenticateToContinue'),
      onClose: modalAuthOnClose,
      onPreSignIn: signOutGlobally,
      onSignInSuccess: handleUpdateUser,
      shouldDisplaySignInSuccessMessage: false,
      shouldSkipDefaultSignInCallback: true,
    });
  }, [email, handleUpdateUser, modalAuthOnClose, setModalAuthState, setUserEmail, signOutGlobally]);

  useEffect(() => {
    if (isMounted) {
      loadUserPreferences();
    }
  }, [isMounted]);

  useEffect(() => {
    if (isNativeIOS()) {
      requestIosAttPermissions();
    }
  }, [requestIosAttPermissions]);

  // prevents to clear the input field while is authenticating
  useEffect(() => {
    if (newPhoneNumber === null && !!user?.details) {
      setNewPhoneNumber(phoneNumber ?? '');
    }
  }, [newPhoneNumber, phoneNumber, user?.details]);

  return (
    <DrawerPage
      headerText={t('preferences')}
      footer={
        smsSubscriptionEnabled && (
          <ActionButton
            disabled={!isNewPhoneNumberValid || isUpdatingUserInfo}
            testID="comm-preferences-update-button"
            fullWidth
            isLoading={isUpdatingUserInfo}
            onPress={showModalAuth}
          >
            {t('update')}
          </ActionButton>
        )
      }
    >
      {featureDisclaimerLoading || syncUserCommPrefsLoading || isLoadingUserPrefs ? (
        <LoyaltyLoader />
      ) : (
        <>
          <CommCardEmailPush
            marketingEmail={marketingEmailPrefs}
            setMarketingEmailPrefs={setMarketingEmailPrefs}
            marketingPush={pushPrefs}
            setPushPrefs={setPushPrefs}
            marketingSms={marketingSmsPrefs}
            setMarketingSmsPrefs={setMarketingSmsPrefs}
            userPhoneNumber={newPhoneNumber ?? ''}
            setOnUpdatePhoneNumber={setOnUpdatePhoneNumber}
            setIsNewPhoneNumberValid={setIsNewPhoneNumberValid}
          />
          <BlockRenderer
            mt="$4"
            mb="$7"
            textProps={{ variant: 'copyTwo' }}
            content={communicationPreferencesBlock?.localeRaw}
          />
        </>
      )}
    </DrawerPage>
  );
};

export default withAuthRequired(CommPreferences);
